import { useState, useRef, useCallback } from 'react';
import i18n from 'i18n-react';
import {
  pickConversationId,
  SmartAssistApiError,
  streamAgentChat,
  streamEventKind,
} from 'services/travProAgentStream';
import type { StreamEvent } from 'services/travProAgentStream';
import type { Message, MessageQuota, TrainingProgress } from './types';
import { savePersistedThread } from './smartAssistStorage';

export interface UseSmartAssistStreamProps {
  resolveEffectiveAppId: () => number | null;
  baseUrl: string;
  getToken: () => Promise<string | null>;
  conversationIdRef: React.MutableRefObject<string | null>;
  msgIdRef: React.MutableRefObject<number>;
  refreshServerConversationsRef: React.MutableRefObject<(() => Promise<void>) | null>;
  /** Called when the server assigns a conversation ID to a new conversation's first message. */
  onMergeConversationIdRef: React.MutableRefObject<ConversationIdMergeHandler | null>;
}

export interface ConversationIdMergeHandler {
  merge(this: void, messageText: string, conversationId: string): void;
}

export interface UseSmartAssistStreamReturn {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  messageQuota: MessageQuota | null;
  trainingProgress: TrainingProgress | null;
  streamAbortRef: React.MutableRefObject<AbortController | null>;
  isStreaming: boolean;
  isQuotaBlocked: boolean;
  sendAgentMessage(messageText: string, opts: { resetConversation: boolean }): Promise<void>;
}

export function useSmartAssistStream({
  resolveEffectiveAppId,
  baseUrl,
  getToken,
  conversationIdRef,
  msgIdRef,
  refreshServerConversationsRef,
  onMergeConversationIdRef,
}: UseSmartAssistStreamProps): UseSmartAssistStreamReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageQuota, setMessageQuota] = useState<MessageQuota | null>(null);
  const [trainingProgress, setTrainingProgress] = useState<TrainingProgress | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  const nextId = (): number => {
    msgIdRef.current += 1;
    return msgIdRef.current;
  };

  const sendAgentMessage = useCallback(
    async (messageText: string, { resetConversation }: { resetConversation: boolean }): Promise<void> => {
      const resolvedAppId = resolveEffectiveAppId();
      if (!resolvedAppId) {
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: 'assistant', text: i18n.translate('smartAssist.appNotReady') as string, streaming: false, links: [] },
        ]);
        return;
      }

      if (resetConversation) conversationIdRef.current = null;

      streamAbortRef.current?.abort();
      streamAbortRef.current = new AbortController();
      const { signal } = streamAbortRef.current;

      const assistantId = nextId();
      setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', text: '', streaming: true, links: [] }]);

      const token = await getToken();
      if (!token) {
        const msg = i18n.translate('smartAssist.signInRequired') as string;
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, streaming: false, text: msg } : m)));
        return;
      }

      const bumpLinks = (links: Message['links']): void => {
        if (!links?.length) return;
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, links } : m)));
      };

      const bumpProgressAndQuota = (source: StreamEvent): void => {
        const quota = source.message_quota ?? source.data?.message_quota;
        const progress = source.training_progress ?? source.data?.training_progress;
        if (quota && typeof quota === 'object') setMessageQuota(quota);
        if (progress && typeof progress === 'object') setTrainingProgress(progress);
      };

      const appendDelta = (chunk: string | undefined): void => {
        if (!chunk) return;
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, text: m.text + chunk } : m)));
      };

      try {
        await streamAgentChat({
          baseUrl, token, appId: String(resolvedAppId), message: messageText,
          conversationId: conversationIdRef.current, signal,
          onEvent: (evt) => {
            bumpProgressAndQuota(evt);
            const cid = pickConversationId(evt);
            if (cid) {
              conversationIdRef.current = cid;
              savePersistedThread(String(resolvedAppId), cid);
              if (resetConversation) onMergeConversationIdRef.current?.merge(messageText, cid);
            }
            const kind = streamEventKind(evt);
            if (kind === 'links' || kind === 'rag_links' || kind === 'citations') bumpLinks(evt.links);
            if (kind === 'delta' || kind === 'message' || kind === 'chunk') {
              appendDelta(
                typeof evt.text === 'string' ? evt.text
                  : typeof evt.content === 'string' ? evt.content
                    : typeof evt.delta === 'string' ? evt.delta
                      : typeof evt.message === 'string' ? evt.message
                        : '',
              );
            }
            if (kind === 'error') {
              setMessages((prev) => prev.map((m) =>
                m.id === assistantId
                  ? { ...m, streaming: false, text: evt.message ?? (i18n.translate('smartAssist.genericError') as string) }
                  : m,
              ));
            }
            if (kind === 'done' || kind === 'complete' || kind === 'finished' || kind === 'end') {
              if (evt.links?.length) bumpLinks(evt.links);
              setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)));
              void refreshServerConversationsRef.current?.();
            }
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)));
          return;
        }
        if (err instanceof SmartAssistApiError && err.status === 429) {
          if (err.messageQuota) setMessageQuota(err.messageQuota);
          else setMessageQuota({ allowed: false, remaining: 0 });
          if (err.trainingProgress) setTrainingProgress(err.trainingProgress);
          if (err.conversationId) {
            conversationIdRef.current = err.conversationId;
            savePersistedThread(String(resolvedAppId), err.conversationId);
          }
        }
        const errMessage = err instanceof Error ? err.message : undefined;
        setMessages((prev) => prev.map((m) =>
          m.id === assistantId
            ? { ...m, streaming: false, text: m.text || errMessage || (i18n.translate('smartAssist.requestFailed') as string) }
            : m,
        ));
      } finally {
        setMessages((prev) => prev.map((m) => (m.id === assistantId && m.streaming ? { ...m, streaming: false } : m)));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolveEffectiveAppId, baseUrl, getToken, conversationIdRef, msgIdRef, refreshServerConversationsRef, onMergeConversationIdRef],
  );

  return {
    messages,
    setMessages,
    messageQuota,
    trainingProgress,
    streamAbortRef,
    isStreaming: messages.some((m) => m.streaming),
    isQuotaBlocked: messageQuota?.allowed === false,
    sendAgentMessage,
  };
}
